import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Fingerprint, ScanLine, History, UserPlus, Loader2, Trash2, Activity, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime, cn, formatApiError } from '../lib/utils';
import { runDiagnostics } from '../lib/fingerprintService';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_BADGE = {
  admin: { label: 'Administrador', className: 'bg-purple-100 text-purple-700 border-purple-200' },
  barber: { label: 'Barbero', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  stylist: { label: 'Estilista', className: 'bg-pink-100 text-pink-700 border-pink-200' },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('es-MX', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Mexico_City',
  });
};

const fmtDuration = (min) => {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, '0')}min`;
};

function DiagRow({ ok, label }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  const cls = ok ? 'text-emerald-600' : 'text-rose-500';
  return (
    <div className="flex items-center gap-2">
      <Icon className={cn('w-4 h-4', cls)} />
      <span className="text-slate-700">{label}</span>
    </div>
  );
}

export default function AsistenciaPage() {
  const { selectedBusiness } = useAuth();
  const [today, setToday] = useState({ date: '', records: [] });
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const runDiag = useCallback(async () => {
    setDiagLoading(true);
    try {
      const result = await runDiagnostics();
      setDiag(result);
    } finally {
      setDiagLoading(false);
    }
  }, []);

  useEffect(() => { runDiag(); }, [runDiag]);

  const load = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const [t, e] = await Promise.all([
        axios.get(`${API_URL}/attendance/today`),
        axios.get(`${API_URL}/fingerprints`),
      ]);
      setToday(t.data);
      setEnrollments(e.data || []);
    } catch (err) {
      logger.error('load asistencia', err);
      toast.error('Error al cargar asistencia');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => { load(); }, [load]);

  const removeEnrollment = async (userId, name) => {
    if (!window.confirm(`¿Eliminar el enrolamiento de huella de ${name}? Deberá volver a enrolarse.`)) return;
    try {
      await axios.delete(`${API_URL}/fingerprints/${userId}`);
      toast.success('Enrolamiento eliminado');
      load();
    } catch (err) {
      toast.error(formatApiError(err, 'Error al eliminar'));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="asistencia-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Asistencia
        </h1>
        <p className="text-slate-500 mt-1">
          Checador biométrico del personal · {selectedBusiness?.name}
        </p>
      </div>

      {/* WebSDK / Lite Client diagnostic */}
      <Card className="border-slate-200" data-testid="fingerprint-diagnostic">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Activity className="w-5 h-5 text-slate-500" />
            Diagnóstico del lector de huella
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={runDiag}
            disabled={diagLoading}
            data-testid="btn-refresh-diag"
          >
            {diagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2">Actualizar</span>
          </Button>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {!diag ? (
            <p className="text-slate-500">Ejecutando diagnóstico…</p>
          ) : (
            <>
              <DiagRow ok={diag.sdk_loaded} label="WebSDK cargado (window.Fingerprint.WebApi)" />
              <DiagRow ok={diag.websdk_channel_ok} label="Canal WebSocket inicializado" />
              <DiagRow
                ok={diag.lite_client_reachable}
                label={
                  diag.lite_client_reachable
                    ? 'Lite Client alcanzable (wss://127.0.0.1:52181)'
                    : 'Lite Client NO responde en wss://127.0.0.1:52181'
                }
              />
              <DiagRow
                ok={diag.deviceCount > 0}
                label={
                  diag.deviceCount > 0
                    ? `Lector(es) detectado(s): ${diag.deviceCount}`
                    : 'No se detectó ningún lector U.are.U conectado'
                }
              />
              {diag.error && (
                <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                  <strong>Detalle:</strong> {diag.error}
                </div>
              )}
              {!diag.lite_client_reachable && diag.sdk_loaded && (
                <div className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-xs space-y-1">
                  <p><strong>Pasos para conectar:</strong></p>
                  <ol className="list-decimal ml-5 space-y-1">
                    <li>Verifica que <strong>HID Authentication Device Client</strong> esté corriendo (ícono en la bandeja del sistema).</li>
                    <li>Abre <a className="text-emerald-700 underline" href="https://127.0.0.1:52181" target="_blank" rel="noreferrer">https://127.0.0.1:52181</a> y acepta el certificado auto-firmado (una sola vez por navegador).</li>
                    <li>Regresa aquí y presiona <strong>Actualizar</strong>.</li>
                  </ol>
                  <p className="mt-2 text-slate-500">
                    Nota: los procesos &ldquo;DigitalPersona Local Agent&rdquo; y &ldquo;Local Host&rdquo; son del SDK de escritorio y no
                    exponen la interfaz web. Se requiere específicamente el <strong>HID Authentication Device Client</strong> (antes &ldquo;Lite Client&rdquo;).
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Action tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/asistencia/checador" data-testid="link-checador">
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white hover:shadow-lg transition cursor-pointer h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500 text-white flex items-center justify-center mb-3">
                <ScanLine className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Ir al Checador
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Pantalla completa para que el personal registre entrada / salida con huella.
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/asistencia/enrolar" data-testid="link-enrolar">
          <Card className="border-slate-200 hover:shadow-lg transition cursor-pointer h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-3">
                <UserPlus className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Enrolar huella
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Registra la huella de un miembro del personal (3 capturas).
              </p>
            </CardContent>
          </Card>
        </Link>

        <Card className="border-slate-200 h-full">
          <CardContent className="p-6">
            <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center mb-3">
              <Fingerprint className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Enrolados
            </h3>
            <p className="text-3xl font-bold mt-1" data-testid="enrollments-count">
              {enrollments.length}
            </p>
            <p className="text-xs text-slate-400">personas con huella registrada</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's records */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <History className="w-5 h-5 text-slate-500" />
            Movimientos de hoy {today.date && <span className="text-sm text-slate-400 font-normal">· {today.date}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : today.records.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Sin registros hoy.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Persona</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Entrada</TableHead>
                  <TableHead>Salida</TableHead>
                  <TableHead>Programado</TableHead>
                  <TableHead className="text-right">Trabajado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {today.records.map((r) => {
                  const badge = ROLE_BADGE[r.user_role] || { label: r.user_role, className: 'bg-slate-100 text-slate-700' };
                  return (
                    <TableRow key={r.id} data-testid={`row-${r.user_id}`}>
                      <TableCell className="font-medium">{r.user_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-xs border', badge.className)}>
                          {badge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono">{fmtTime(r.check_in_at)}</TableCell>
                      <TableCell className="font-mono">{fmtTime(r.check_out_at)}</TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {r.scheduled_start && r.scheduled_end
                          ? `${r.scheduled_start} – ${r.scheduled_end}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {fmtDuration(r.worked_minutes)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Enrolled personnel */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Personal enrolado</CardTitle>
        </CardHeader>
        <CardContent>
          {enrollments.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">
              Aún no hay enrolamientos. <Link to="/asistencia/enrolar" className="text-emerald-600 underline">Enrolar el primero</Link>.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Persona</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Enrolado</TableHead>
                  <TableHead>Calidad</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrollments.map((e) => {
                  const badge = ROLE_BADGE[e.user_role] || { label: e.user_role, className: 'bg-slate-100 text-slate-700' };
                  return (
                    <TableRow key={e.id} data-testid={`enrollment-${e.user_id}`}>
                      <TableCell className="font-medium">{e.user_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-xs border', badge.className)}>
                          {badge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{formatDateTime(e.enrolled_at)}</TableCell>
                      <TableCell>
                        <span className={cn(
                          'text-xs font-medium',
                          e.quality >= 70 ? 'text-emerald-600' : e.quality >= 40 ? 'text-amber-600' : 'text-red-600',
                        )}>
                          {e.quality || 0}/100
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="icon" variant="ghost"
                          className="h-8 w-8 hover:text-red-600"
                          onClick={() => removeEnrollment(e.user_id, e.user_name)}
                          data-testid={`delete-enrollment-${e.user_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
